import { JSX, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'

import { PortalChild } from './pinOutPortal/PortalChild'
import { WidgetPlaceholder } from './pinOutPortal/WidgetPlaceholder'

export interface IPosition {
  top: string
  left: string
}

interface IProps {
  allowPinOut: boolean
  pinOut: boolean
  moveWidget: boolean
  widgetId: string
  setPinOut: (arr: boolean) => void
  children: JSX.Element | Array<JSX.Element>
  appAbsoluteRoot: HTMLElement | null
  hidePinOut?: boolean
}

export const PinOutPortal = ({
  allowPinOut,
  pinOut,
  moveWidget,
  widgetId,
  setPinOut,
  appAbsoluteRoot,
  children,
  hidePinOut,
}: IProps) => {
  const [height, setHeight] = useState<string>('auto')
  const [initialPos, setInitialPos] = useState<IPosition>({ top: '', left: '' })
  const [savedPos, setSavedPos] = useState<IPosition>({ top: '', left: '' })
  const [width, setWidth] = useState<string>('')
  const [pinoutStyle, setPinoutStyle] = useState({
    width: '',
    top: '',
    left: '',
  })

  useEffect(() => {
    if (
      (!moveWidget && width !== pinoutStyle.width) ||
      initialPos.top !== pinoutStyle.top ||
      initialPos.left !== pinoutStyle.left
    ) {
      setPinoutStyle({
        width: `${width}`,
        top: initialPos.top,
        left: initialPos.left,
      })
    }
  }, [initialPos])

  useEffect(() => {
    setPinoutStyle({ ...pinoutStyle, width })
  }, [width])

  useEffect(() => {
    if (!pinOut) {
      setSavedPos({ top: '', left: '' })
    } else {
      const res = localStorage.getItem('pin_out_widgets')
      const obj = JSON.parse(res || '{}')
      if (obj[widgetId]) {
        setPinoutStyle({
          width: `${width}`,
          top: obj[widgetId].top || initialPos.top || '',
          left: obj[widgetId].left || initialPos.left || '',
        })
        setSavedPos({
          top: obj[widgetId].top || '',
          left: obj[widgetId].left || '',
        })
        if (obj[widgetId].width) {
          setWidth(obj[widgetId].width)
        }
        if (obj[widgetId].height) {
          setHeight(obj[widgetId].height)
        }
      }
    }
  }, [pinOut])

  const childWrapper = (
    <PortalChild
      moveWidget={moveWidget}
      pinOut={pinOut}
      widgetId={widgetId}
      setSavedPos={(pos) => {
        setPinoutStyle({
          width: `${width}`,
          top: pos.top || initialPos.top || '',
          left: pos.left || initialPos.left || '',
        })
      }}
      style={{
        position: 'fixed',
        width: `${pinoutStyle.width}px`,
        height: `${+height > 200 ? height : 200}px`,
        transform: `translate(${pinoutStyle.left}px, ${pinoutStyle.top}px)`,
      }}
    >
      {children}
    </PortalChild>
  )
  return (
    <>
      {allowPinOut && !pinOut && !savedPos.top ? (
        <>
          <WidgetPlaceholder
            hidePinOut={hidePinOut}
            setWidth={(w: string) => {
              setWidth(w)
            }}
            setHeight={(h: string) => setHeight(h)}
            setInitialPos={(pos: IPosition) => {
              !moveWidget && setInitialPos({ ...pos })
            }}
            width={width}
          >
            {children}
          </WidgetPlaceholder>
        </>
      ) : (allowPinOut && pinOut) || savedPos.top ? (
        <>
          {appAbsoluteRoot && ReactDOM.createPortal(childWrapper, appAbsoluteRoot)}
          <WidgetPlaceholder
            hidePinOut={hidePinOut}
            setWidth={
              !hidePinOut
                ? (w: string) => {
                    setWidth(w)
                  }
                : undefined
            }
            height={height}
          />
        </>
      ) : (
        children
      )}
    </>
  )
}
